/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { matchPath } from 'react-router';
import { UserRoles } from './user';
import { RouteInterface, RoutesInterface } from '../types/routes';
import { statsRoutes } from '../routes/stats/routes';
import { sdgGapRoutes } from '../routes/sdgap/routes';
import { worldTrackerRoutes } from '../routes/world-tracker/routes';
import { emissionsCalculatorsRoutes } from '../apps/carbon-calculator/routes';
import { materialityTrackerRedirect, materialityTrackerRoutes } from '../apps/materiality-tracker/routes/routes';
import { companyTrackerRoutes } from '../routes/company-tracker/routes';
import { portfolioTrackerRoutes } from '../apps/portfolio-tracker/routes/routes';
import { assuranceRoutes } from '../apps/assurance/routes/routes';
import { companyTrackerLightOnboardingRoutes } from '../routes/company-tracker-light-onboarding/routes';

// NOTE: The order of this is important. It is the order in which routes are decided to render,
//       so put specific matches on top of broad ones!
export const nonAppRoutes: RoutesInterface = {
  USER_PREFERENCES: {
    id: 'user_preferences',
    label: 'User Preferences',
    path: '/user-preferences',
  },
  USER_PROFILE: {
    id: 'user_profile',
    label: 'User Profile',
    path: '/user-profile/:id?',
  },
  UTRV_PROVENANCE: {
    id: 'utrv_provenance',
    label: 'Universal Tracker Provenance',
    path: '/universal-tracker-provenance/:utrId/:utrvId',
    icon: 'fa-universal-access',
    hidden: false,
    auth: true,
  },
  PUBLIC_SDG_CHART: {
    id: 'public_sdg_chart',
    label: 'SDG Contribution Chart',
    path: '/public/:initiativeId/sdg-contribution-chart/:token',
    hidden: true,
    auth: false,
  },
  SHARED_CUSTOM_DASHBOARD: {
    id: 'shared_custom_dashboard',
    label: 'Shared Custom Dashboard',
    path: '/custom-dashboard/:dashboardId/token/:token',
    hidden: true,
    auth: false,
  },
  SGX_ESGENOME: {
    id: 'sgx_esgenome',
    label: 'SGX ESGenome',
    path: '/sgxesgenome',
  },
};

export const ROUTES: RoutesInterface = {
  ...worldTrackerRoutes,
  ...companyTrackerRoutes,
  ...portfolioTrackerRoutes,
  ...assuranceRoutes,
  ...sdgGapRoutes,
  ...emissionsCalculatorsRoutes,
  ...statsRoutes,
  ...materialityTrackerRedirect,
  ...materialityTrackerRoutes,
  ...companyTrackerLightOnboardingRoutes,
  ...nonAppRoutes,
};

export const initiativeRoutes = [ROUTES.COMPANY_TRACKER_LIST, ROUTES.SUMMARY, ROUTES.DOWNLOADS, ROUTES.CUSTOM_METRICS];

export const getRouteTitle = (path: string) => {
  const defaultTitle = 'G17Eco Sustainability Reporting';

  if (!path || path === '/') {
    return defaultTitle;
  }

  const combinedRoutes = [...initiativeRoutes, ...Object.values(ROUTES).reverse()];

  const currentRoute = combinedRoutes.find((r) => {
    const match = matchPath(path, {
      path: r.path,
      exact: !!r.exact,
    });
    return Boolean(match);
  });

  return currentRoute ? currentRoute.label : defaultTitle;
};

const userHasPermission = (requiredRoles?: UserRoles[], userPermissionsArray?: UserRoles[]) => {
  if (!requiredRoles) {
    if (userPermissionsArray && userPermissionsArray.length === 1 && userPermissionsArray[0] === UserRoles.Guest) {
      // If they are exclusively a guest, then I think we want to default to no-access?
      return false;
    }
    return true;
  }

  if (!userPermissionsArray) {
    return false;
  }

  return requiredRoles.some((requiredRole) => userPermissionsArray.includes(requiredRole));
};

export const isRouteEnabled = (
  route: Pick<RouteInterface, 'requiredUserRoles' | 'navItemPermissionId'>,
  userPermissionsArray: UserRoles[],
  reportingLevelDashboards: string[],
) => {
  if (!reportingLevelDashboards) {
    return [false, false];
  }

  const isUserAllowed = userHasPermission(route.requiredUserRoles, userPermissionsArray);
  const navItemEnabled = !route.navItemPermissionId || reportingLevelDashboards.includes(route.navItemPermissionId);

  return [navItemEnabled, isUserAllowed];
};
